import FilterOption from '@components/FilterOption';
import { filterOptionItemSettings } from '@components/FilterOptionItem';
import { type FilterOptionType } from '@providers/FilterProvider';
import type { MaybeArrayItem } from '@types';
import { isMobileWidth, stripHtml, toItemArray } from '@utils';
import { debounce, isArray } from 'lodash-es';
import { useEffect, useRef } from 'react';

import type { FilterOptionProps } from '../FilterOption';
import type { FilterOptionBoxItemProps as ItemProps } from './Item';
import FilterOptionBoxItem from './Item';

export type FilterOptionBoxProps<FilterOption extends FilterOptionType> = {
  option: FilterOption;
  filterOptionProps?: Partial<FilterOptionProps>;
  filterOptionItemProps: (
    optionItem: MaybeArrayItem<FilterOption['values']>
  ) => Pick<ItemProps, 'value' | 'docCount'>;
  settings: {
    filterOptionBoxCharWidth: number;
    filterLayout: 'horizontal' | 'vertical';
    filterTreeHorizontalStyle: 'style1' | 'style2' | 'style3' | 'style-expand';
  };
};

const FilterOptionBox = <FilterOption extends FilterOptionType>({
  option,
  filterOptionItemProps,
  filterOptionProps,
  settings,
}: FilterOptionBoxProps<FilterOption>) => {
  const { filterOptionBoxCharWidth, filterLayout, filterTreeHorizontalStyle } = settings;

  const rootElmRef = useRef<HTMLUListElement | null>(null);
  const itemSettings = filterOptionItemSettings(option);
  const filterOptionsRef = useRef<FilterOptionType['values'] | null>([]);

  const calcOptionItemWidth = (filterOptions: FilterOptionType['values']): string | undefined => {
    const rootElm = rootElmRef.current;
    if (!rootElm) return;
    let columnWidth = 4;

    toItemArray(filterOptions).forEach((filterItem, index) => {
      const optionValue = filterItem.key;
      const textWidth = stripHtml(optionValue).length * filterOptionBoxCharWidth;
      columnWidth = Math.max(textWidth, columnWidth);
    });

    const marginItem = isMobileWidth() ? 15 : 8;
    const extraWidth = (marginItem + 5) * 2; // (margin + padding item)*2
    let numberOfColumns = 1;

    if (filterLayout === 'vertical' || isMobileWidth()) {
      numberOfColumns = Math.floor(rootElm.clientWidth / (columnWidth + extraWidth)) || 1;
    } else {
      switch (filterTreeHorizontalStyle) {
        case 'style1':
          numberOfColumns = 1;
          break;
        case 'style2':
          numberOfColumns = 2;
          break;
        case 'style3':
          numberOfColumns = 6;
          break;
        case 'style-expand':
          numberOfColumns = 2;
          break;

        default:
          break;
      }
    }

    const lengthOfFilterOptions = isArray(filterOptions) ? filterOptions?.length : 1;
    const gridTemplateCols: string = Array(Math.min(lengthOfFilterOptions, numberOfColumns))
      .fill(null)
      .reduce((template) => (template += ' 1fr'), '');

    Object.assign(rootElm.style, {
      display: 'grid',
      'grid-template-columns': gridTemplateCols,
    });
  };

  useEffect(() => {
    if (filterOptionsRef.current && rootElmRef.current) {
      const reCalcItemWidth = debounce(() => {
        if (filterOptionsRef.current) {
          calcOptionItemWidth(filterOptionsRef.current);
        }
      }, 500);

      const resize_ob = new ResizeObserver(function () {
        reCalcItemWidth();
      });

      resize_ob.observe(rootElmRef.current);

      return () => {
        resize_ob.disconnect();
      };
    }
  });

  return (
    <FilterOption
      option={option}
      {...filterOptionProps}
      listItemModifiers={['inline']}
      listItemRef={rootElmRef}
    >
      {({ filterOptions }) => {
        filterOptionsRef.current = filterOptions;

        return toItemArray(filterOptions).map((optionItem) => {
          const optionItemProps = filterOptionItemProps(optionItem);
          return (
            <FilterOptionBoxItem
              key={optionItemProps.value}
              prefix={option.prefix}
              filterOptionId={option.filterOptionId}
              settings={itemSettings}
              {...optionItemProps}
              {...filterOptionProps}
            />
          );
        });
      }}
    </FilterOption>
  );
};

export default FilterOptionBox;
